// The offer's quantity-tier pre-dialog (client/src/components/quantitytiers/offer.vue): which
// positions offer tiers, and the custom field that carries the choice to the server, which prints
// one extra row per chosen tier (server/src/ProdFlop.Server.Services/QuantityTierOffer.cs).
// Pure, so node --test runs it: client/tests/quantityTiersOffer.test.mjs.

import { tierThresholds } from './quantityTiersEditor.mjs'

// Must match QuantityTierOffer.SelectionField.
export const OFFER_TIERS_FIELD = 'quantityTiers'

// The object actions that print an inquiry offer (ObjectActionType.InquiryOffer / InquiryOfferEn).
const OFFER_ACTIONS = ['InquiryOffer', 'InquiryOfferEn']

export function isOfferAction(action) {
  return !!action && OFFER_ACTIONS.includes(action.action)
}

// One choice per saved position whose product has tiers, in position order. The server finds
// positions by id, and a position has one only once the inquiry is saved.
export function offerTierChoices(inquiry) {
  return ((inquiry && inquiry.items) || [])
    .map((item, index) => ({ item, position: index + 1 }))
    .filter(({ item }) => item && item.id > 0 && item.component)
    .map(({ item, position }) => ({
      itemId: item.id,
      position,
      name: item.component.name,
      externalRefId: item.component.externalRefId,
      quantity: item.quantity,
      thresholds: tierThresholds(item.component),
    }))
    .filter(choice => choice.thresholds.length > 0)
}

// The tier in force at the position's own quantity - the one an order for it would be priced at -
// and every tier above it start selected; lower tiers start unchecked (spec 8).
export function defaultOfferTierSelection(choices) {
  return choices.map(choice => {
    const quantity = Number(choice.quantity)
    const inForce = choice.thresholds.filter(threshold => threshold <= quantity)
    const inForceThreshold = inForce.length > 0 ? Math.max(...inForce) : null
    return {
      itemId: choice.itemId,
      thresholds: choice.thresholds.filter(threshold => !(threshold <= quantity) || threshold === inForceThreshold),
    }
  })
}

// {itemId}@{threshold},{threshold}+{itemId}@{threshold} - thresholds ascending and distinct,
// positions with nothing selected left out. ';' separates the custom fields themselves on the
// download URL, so positions are joined by '+'.
export function encodeOfferTierSelection(selection) {
  return selection
    .map(({ itemId, thresholds }) => ({ itemId, thresholds: [...new Set(thresholds)].sort((a, b) => a - b) }))
    .filter(({ thresholds }) => thresholds.length > 0)
    .map(({ itemId, thresholds }) => `${itemId}@${thresholds.join(',')}`)
    .join('+')
}

// The custom fields the offer action gains: none when nothing is selected, so such an offer
// is requested exactly as before.
export function offerTierCustomFields(selection) {
  const value = encodeOfferTierSelection(selection)
  return value ? [{ variableName: OFFER_TIERS_FIELD, value }] : []
}
